import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/types";

interface Props {
  items: Drama[];
}

export default function HeroCarousel({ items }: Props) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % items.length), 6500);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  const current = items[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点推荐"
    >
      <div className="relative h-[520px] w-full md:h-[620px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.wide}
              alt={`${item.title} 焦点剧照`}
              title={`${item.title} 日本伦理剧推荐`}
              className={`h-full w-full object-cover object-top ${i === index ? "animate-kenburns" : ""}`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/40" />

        <div className="container-page relative flex h-full flex-col justify-end pb-12 md:pb-16">
          <div className="w-full max-w-2xl">
            <div className="mb-4 flex items-center gap-2">
              <span className="rounded-sm bg-accent-500 px-2 py-1 font-label text-[10px] font-semibold tracking-[0.16em] text-primary-950">
                EDITOR'S PICK
              </span>
              <span className="font-label text-[11px] tracking-[0.2em] text-background-200">
                {current.year} / {current.broadcaster}
              </span>
            </div>
            <h1 key={current.id} className="font-heading text-[30px] font-semibold leading-tight text-background-50 animate-fade-up md:text-[46px]">
              {current.title}
            </h1>
            <p className="mt-3 font-heading text-sm text-accent-200 md:text-base">「{current.tagline}」</p>
            <p className="mt-4 line-clamp-3 max-w-xl text-[13px] leading-relaxed text-background-200 md:text-[15px]">
              {current.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                阅读剧集详情
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/category/ethics-drama"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200/50 px-5 py-3 text-sm text-background-100 transition-colors hover:border-accent-300 hover:text-accent-200"
              >
                浏览全部剧集
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-end justify-between gap-4">
            <div className="flex items-center gap-2" role="tablist" aria-label="切换焦点内容">
              {items.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`查看 ${item.title}`}
                  onClick={() => setIndex(i)}
                  className={`h-[3px] cursor-pointer rounded-full transition-all duration-500 ${
                    i === index ? "w-10 bg-accent-400" : "w-5 bg-background-200/40 hover:bg-background-200/70"
                  }`}
                />
              ))}
              <span className="ml-3 font-label text-[11px] tracking-[0.18em] text-background-300">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </div>
            <div className="hidden items-center gap-2 md:flex">
              <button
                type="button"
                aria-label="上一个"
                onClick={() => setIndex((prev) => (prev - 1 + items.length) % items.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/40 text-background-100 transition-colors hover:border-accent-300 hover:text-accent-200"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                type="button"
                aria-label="下一个"
                onClick={() => setIndex((prev) => (prev + 1) % items.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/40 text-background-100 transition-colors hover:border-accent-300 hover:text-accent-200"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}